import { UserState } from "@erinfo/consumer/src/store/models/user"
import { parseNumber } from "@erinfo/react-utils/src/helpers/transform-text"
import { MedicalServices } from "@mui/icons-material"
import { Box, Typography } from "@mui/material"
import { SxProps } from "@mui/system"
import dayjs from "dayjs"
import { FC } from "react"

import {
  AddEditAction,
  MedicalDataValue,
  ProfileValueDisplay,
  StyledDivider,
  StyledFormCard,
} from "./common"

export const MedicalPanel: FC<{
  user: UserState
  onAdd: () => void
  sx: SxProps
}> = ({ user, onAdd, sx }) => {
  const { allergies, medicalConditions, medications, medicalDevices } =
    user.medicalData || {}
  const { directives, profile } = user
  const {
    preferredTreatmentHospital,
    primaryCarePhysician,
    primaryCarePhysicianPhone,
  } = profile || {}

  const dataPresent =
    (user.medicalData &&
      Object.values(user.medicalData).some((i) => !!i?.length)) ||
    (user.directives && Object.values(user.directives).some((i) => i))

  return (
    <StyledFormCard
      id="medicalPanel"
      Icon={<MedicalServices sx={{ color: `medical.main` }} />}
      headerText="Medical"
      sx={sx}
      contentSx={{ overflowY: `auto` }}
      Action={() => <AddEditAction edit={dataPresent} onClick={onAdd} />}
    >
      <Typography variant="h7" mb={4}>
        Conditions
      </Typography>
      {medicalConditions?.length ? (
        medicalConditions?.map(({ title, onset }, idx) => (
          <Box sx={{ marginBottom: 1, ml: 2 }} key={`${title}${idx}`}>
            <Typography>
              {title === `None` ? `None - No Medical Conditions` : title}
            </Typography>
            <MedicalDataValue
              label="Onset Date:"
              value={onset ? dayjs(onset).format(`LL`) : null}
            />
          </Box>
        ))
      ) : (
        <Typography sx={{ opacity: 0.5, mb: 1, ml: 2 }}>
          Not yet entered
        </Typography>
      )}

      <Typography variant="h7" mb={4} mt={4}>
        Allergies
      </Typography>
      {allergies?.length ? (
        allergies?.map(({ title, reaction, reactionDesc }) => (
          <Box sx={{ marginBottom: 1, ml: 2 }} key={title}>
            <Typography component="span">
              {title === `None` ? `None - No Allergies` : title}
            </Typography>
            <Typography
              color="error"
              component="span"
              sx={{ fontSize: `0.8rem`, ml: 2, fontWeight: 500 }}
            >
              {reaction && `ANAPHYLAXIS`}
              <br />
            </Typography>
            <MedicalDataValue label="Reaction:" value={reactionDesc} />
          </Box>
        ))
      ) : (
        <Typography sx={{ opacity: 0.5, mb: 1, ml: 2 }}>
          Not yet entered
        </Typography>
      )}

      <Typography variant="h7" mb={4} mt={4}>
        Medications
      </Typography>
      {medications?.length ? (
        medications?.map(({ title, dosage, frequency }) => (
          <Box sx={{ marginBottom: 1, ml: 2, mb: 1 }} key={title}>
            <Typography>
              {title === `None` ? `None - No Medications` : title}
            </Typography>
            <MedicalDataValue label="Dosage:" value={dosage} />
            <MedicalDataValue label="Frequency:" value={frequency} />
          </Box>
        ))
      ) : (
        <Typography sx={{ opacity: 0.5, mb: 1, ml: 2 }}>
          Not yet entered
        </Typography>
      )}

      <Typography variant="h7" mb={4} mt={4}>
        Medical Devices & Implants
      </Typography>
      {medicalDevices?.length ? (
        medicalDevices?.map(({ title, serialNumber, implantedDate }) => (
          <Box sx={{ marginBottom: 1, ml: 2 }} key={title}>
            <Typography>
              {title === `None` ? `None - No Medical Devices` : title}
            </Typography>
            <MedicalDataValue label="Serial Number:" value={serialNumber} />
            <MedicalDataValue
              label="Implant Date:"
              value={implantedDate ? dayjs(implantedDate).format(`LL`) : null}
            />
          </Box>
        ))
      ) : (
        <Typography sx={{ opacity: 0.5, mb: 1, ml: 2 }}>
          Not yet entered
        </Typography>
      )}

      <Typography variant="h7" mb={4} mt={4}>
        Advanced Directives
      </Typography>
      {!!directives && (
        <Box sx={{ marginBottom: 1, ml: 2 }}>
          <MedicalDataValue
            label="I am an Organ Donor:"
            value={directives?.organDonor ? `YES` : `NO`}
          />
          <MedicalDataValue
            label="I have Advanced Directives:"
            value={directives?.hasDirectives ? `YES` : `NO`}
          />
          {directives?.hasDirectives && (
            <Box pl={2}>
              <MedicalDataValue
                label="Where found:"
                value={directives.location}
              />
              {(directives.name || directives.phone) && (
                <MedicalDataValue
                  label="Who has access:"
                  value={`${directives.name ?? ``} ${
                    (directives.phone && parseNumber(directives.phone)) ?? ``
                  }`}
                />
              )}
            </Box>
          )}
        </Box>
      )}

      <Typography variant="h7" mb={4} mt={4}>
        Preferred Treatment Hospital
      </Typography>
      <ProfileValueDisplay
        label=""
        value={preferredTreatmentHospital}
        sx={{ ml: 2 }}
      />
      <Typography variant="h7" mb={4} mt={4}>
        Primary Care Physician
      </Typography>
      <ProfileValueDisplay
        label=""
        sx={{ ml: 2 }}
        value={
          (primaryCarePhysician || primaryCarePhysicianPhone) &&
          `${primaryCarePhysician ?? ``} ${
            primaryCarePhysicianPhone
              ? `\u2022 ${parseNumber(primaryCarePhysicianPhone)}`
              : ``
          }`
        }
      />

      <StyledDivider />

      <ProfileValueDisplay
        label="Notes"
        value={user?.notesMedical}
        sx={{ whiteSpace: `normal` }}
      />
    </StyledFormCard>
  )
}
